import { format } from 'date-fns';
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  ExternalLink,
  FileText,
  Loader2,
  Mail,
  MessageSquare,
  MinusCircle,
  Send,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { FrontEmailModal } from '@/components/front-email-viewer';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useGetTaskDeliverablesQuery } from '@/services/api/deliverablesService';
import { useGetFrontConversationByDbIdQuery } from '@/services/api/frontMessageService';
import type { Deliverable } from '@/services/types/deliverables-types';
import {
  DeliverableMatchStatus,
  DeliverableStatus,
} from '@/services/types/deliverables-types';

// ============================================================================
// Types
// ============================================================================

interface CoverageTask {
  id: string;
  label: string;
  type: 'send_lor' | 'ack' | 'other';
}

interface CoverageDeliverableMatchesProps {
  matterId: string;
  tasks: CoverageTask[];
}

// ============================================================================
// Sub-components
// ============================================================================

const MatchRow: React.FC<{
  match: Deliverable['matches'][0];
  matterId: string;
  onOpenEmail: (conversationId: string) => void;
}> = ({ match, matterId, onOpenEmail }) => {
  // Fetch conversation details if this is an email match
  const { data: conversationData } = useGetFrontConversationByDbIdQuery(
    { matterId, conversationDbId: match.front_conversation_id ?? '' },
    { skip: !match.front_conversation_id },
  );

  const isEmail = !!match.front_conversation_id;
  const isDocument = !!match.artifact_id;

  const subject = conversationData?.conversation?.subject || match.artifact_name;
  const recipient = conversationData?.conversation?.recipient?.handle;
  const createdAt = match.created_at
    ? format(new Date(match.created_at), 'MMM d, yyyy h:mm a')
    : '-';

  const statusColor: Record<string, string> = {
    [DeliverableMatchStatus.APPROVED]: 'text-emerald-600 bg-emerald-50',
    [DeliverableMatchStatus.NEEDS_REVIEW]: 'text-amber-600 bg-amber-50',
    [DeliverableMatchStatus.REJECTED]: 'text-red-600 bg-red-50',
  };

  return (
    <div className="flex items-center gap-3 px-3 py-2 text-xs border-b last:border-b-0 hover:bg-zinc-50">
      {/* Type icon */}
      <div className="flex-shrink-0">
        {isEmail ? (
          <Mail className="h-3.5 w-3.5 text-blue-500" />
        ) : isDocument ? (
          <FileText className="h-3.5 w-3.5 text-zinc-500" />
        ) : (
          <MessageSquare className="h-3.5 w-3.5 text-zinc-400" />
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate" title={subject || undefined}>
            {subject || 'Untitled'}
          </span>
          {match.status && (
            <span
              className={cn(
                'px-1.5 py-0.5 rounded text-[10px] font-medium',
                statusColor[match.status] || 'text-zinc-600 bg-zinc-100',
              )}
            >
              {match.status}
            </span>
          )}
        </div>
        {recipient && (
          <div className="text-zinc-500 truncate" title={recipient}>
            To: {recipient}
          </div>
        )}
      </div>

      {/* Date */}
      <div className="flex-shrink-0 text-zinc-500">{createdAt}</div>

      {/* Actions */}
      <div className="flex-shrink-0">
        {isEmail && match.front_conversation_id && (
          <button
            type="button"
            onClick={() => onOpenEmail(match.front_conversation_id ?? '')}
            className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
            title="View email thread"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

const DeliverableSection: React.FC<{
  deliverable: Deliverable;
  matterId: string;
  taskLabel: string;
  onOpenEmail: (conversationId: string) => void;
  defaultOpen?: boolean;
}> = ({ deliverable, matterId, taskLabel, onOpenEmail, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const matchCounts = useMemo(() => {
    const approved = deliverable.matches.filter(
      (m) => m.status === DeliverableMatchStatus.APPROVED,
    ).length;
    const needsReview = deliverable.matches.filter(
      (m) => m.status === DeliverableMatchStatus.NEEDS_REVIEW,
    ).length;
    return { approved, needsReview, total: deliverable.matches.length };
  }, [deliverable.matches]);

  const statusIcon: Record<string, React.ReactNode> = {
    [DeliverableStatus.SATISFIED]: (
      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
    ),
    [DeliverableStatus.PENDING]: <Clock className="h-4 w-4 text-amber-500" />,
    [DeliverableStatus.BYPASSED]: (
      <MinusCircle className="h-4 w-4 text-zinc-400" />
    ),
  };

  if (deliverable.matches.length === 0) {
    return null;
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 transition-colors"
        >
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-zinc-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-zinc-400" />
          )}

          {statusIcon[deliverable.status || DeliverableStatus.PENDING]}

          <div className="flex-1 text-left">
            <span className="text-sm font-medium">{deliverable.name}</span>
            <span className="text-xs text-zinc-500 ml-2">({taskLabel})</span>
          </div>

          <div className="flex items-center gap-2 text-xs">
            {matchCounts.approved > 0 && (
              <span className="text-emerald-600">✓{matchCounts.approved}</span>
            )}
            {matchCounts.needsReview > 0 && (
              <span className="text-amber-600">?{matchCounts.needsReview}</span>
            )}
            <span className="text-zinc-400">
              {matchCounts.total} match{matchCounts.total !== 1 ? 'es' : ''}
            </span>
          </div>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="border-t bg-white">
          {deliverable.matches.map((match) => (
            <MatchRow
              key={match.id}
              match={match}
              matterId={matterId}
              onOpenEmail={onOpenEmail}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// ============================================================================
// Task Deliverables Fetcher - separate component to handle hook per task
// ============================================================================

interface TaskDeliverablesFetcherProps {
  matterId: string;
  task: CoverageTask;
  onOpenEmail: (conversationId: string) => void;
}

const TaskDeliverablesFetcher: React.FC<TaskDeliverablesFetcherProps> = ({
  matterId,
  task,
  onOpenEmail,
}) => {
  const { data: deliverables, isLoading } = useGetTaskDeliverablesQuery(
    { matterId, taskId: task.id },
    { skip: !task.id },
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading {task.label}...
      </div>
    );
  }

  if (!deliverables || deliverables.length === 0) {
    return null;
  }

  // Filter to only deliverables with matches
  const deliverablesWithMatches = deliverables.filter(
    (d) => d.matches && d.matches.length > 0,
  );

  if (deliverablesWithMatches.length === 0) {
    return null;
  }

  return (
    <>
      {deliverablesWithMatches.map((deliverable) => (
        <DeliverableSection
          key={deliverable.id}
          deliverable={deliverable}
          matterId={matterId}
          taskLabel={task.label}
          onOpenEmail={onOpenEmail}
        />
      ))}
    </>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const CoverageDeliverableMatches: React.FC<
  CoverageDeliverableMatchesProps
> = ({ matterId, tasks }) => {
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);

  const handleOpenEmail = (conversationId: string) => {
    setSelectedConversationId(conversationId);
  };

  if (tasks.length === 0) {
    return (
      <div className="rounded border p-4 bg-white text-center text-sm text-zinc-500">
        No tasks found for this coverage.
      </div>
    );
  }

  return (
    <>
      <div className="rounded border bg-white overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 bg-zinc-50 border-b">
          <div className="flex items-center gap-2">
            <Send className="h-4 w-4 text-zinc-500" />
            <span className="text-sm font-semibold">Communications</span>
          </div>
        </div>

        {/* Content - render each task's deliverables */}
        <div className="divide-y">
          {tasks.map((task) => (
            <TaskDeliverablesFetcher
              key={task.id}
              matterId={matterId}
              task={task}
              onOpenEmail={handleOpenEmail}
            />
          ))}
        </div>

        {/* Empty state - shown when component mounts, will be replaced by task fetchers */}
        {tasks.length > 0 && (
          <div className="empty-state-placeholder" />
        )}
      </div>

      {/* Email viewer modal */}
      {selectedConversationId && (
        <FrontEmailModal
          matterId={matterId}
          conversationDbId={selectedConversationId}
          isOpen={!!selectedConversationId}
          onOpenChange={(open) => {
            if (!open) setSelectedConversationId(null);
          }}
        />
      )}
    </>
  );
};

export default CoverageDeliverableMatches;
